import React, { MouseEvent, useRef } from "react";

import { useTranslation } from "next-i18next";

import useToggleFavorite from "Apollo/hooks/useToggleFavorite";

import ProductUtils from "./utils/ProductUtils";
import { ProductBySlugNonNullable } from "@/common/contracts/pdp.contracts";
import { useGuestModalGuard } from "@/common/hooks/guest.hooks";
import { cn } from "@/common/utils/css.utils";
import { useProWorkspace } from "@/features/pro-buyer-workspaces/components/workspace-context";
import { Heading } from "@/ui/cva/Heading";
import HeartFilled from "@whoppah/icons/24/heart/Filled";
import HeartOutlined from "@whoppah/icons/24/line/Heart";

type ProductDetailsFavoriteButtonProps = {
  product: ProductBySlugNonNullable;
  transparent?: boolean;
};

export const ProductDetailsFavoriteButton = ({
  product,
  transparent = true,
}: ProductDetailsFavoriteButtonProps) => {
  const { toggleFavorite, isFavorite } = useToggleFavorite(product);
  const { t } = useTranslation();
  const { guestGuard } = useGuestModalGuard();
  /* WHO-6629: Pro Buyers inside the workspace gate get the project menu; everyone else, a no-op. */
  const workspace = useProWorkspace();
  const buttonRef = useRef<HTMLButtonElement>(null);

  const isSold = ProductUtils.isSold(product);

  const handleFavoriteClick = async (evt: MouseEvent<HTMLButtonElement>) => {
    evt.preventDefault();

    const pass = await guestGuard({
      title: (
        <span className="flex flex-col space-y-2">
          <HeartFilled className="mx-auto h-8 w-8 text-attention-500" />
          <Heading as="h4" className="text-center">
            {t("signup-login-wall:signup-login-wall-favorite-title")}
          </Heading>
        </span>
      ),
      description: t("signup-login-wall:signup-login-wall-favorite-desc"),
      referrer: "pdp",
    });

    if (!pass) {
      return;
    }

    /* R2: a piece that sits in projects asks before the heart empties. */
    if (isFavorite && !(await workspace.confirmUnfavorite(product.id))) {
      return;
    }

    try {
      const outcome = await toggleFavorite();

      if (outcome === "added") {
        workspace.afterFavoriteSaved(product.id, buttonRef.current);
      }
    } catch (ex) {
      console.error(ex);
    }
  };

  if (isSold) {
    return null;
  }

  return (
    <button
      ref={buttonRef}
      onClick={handleFavoriteClick}
      aria-label={
        isFavorite
          ? t("common:remove-from-favorites")
          : t("common:add-to-favorites")
      }
      aria-pressed={isFavorite}
      /* WHO-6629 F25: lets the project menu find this heart again after a re-render. */
      data-favorite-heart={product.id}
      className={cn(
        "flex h-9 w-9 cursor-pointer  items-center justify-center rounded-full shadow-[0_2px_4px_rgba(0,0,0,0.1)]",
        !transparent && "bg-white",
        transparent && "border-[1px] text-grey-700 hover:bg-grey-700/[0.1]"
      )}
    >
      {isFavorite ? (
        <HeartFilled className="text-attention-500" />
      ) : (
        <HeartOutlined className="text-grey-700" />
      )}
    </button>
  );
};
